import { apiClient } from '../../../api/client'
import { pruneEmpty } from '../../../lib/download'
import type { PagedResponse } from '../../meetings/types'
import type { ActionDocument, MinutePerson } from '../../actions/types'
import type {
  CloseItem,
  MeetingGroupOption,
  MeetingManagementDisplaySettings,
  MeetingManagementQuery,
  MeetingManagementTask,
  ReopenRequest,
  ResponsibleRole,
  TaskCreatorOption,
} from '../types'

/**
 * Toplantı Yönetimi ekranının istemcisi (105) — sekiz uç,
 * `contracts/meeting-management-api.md` ile birebir.
 */
const BASE = '/management/meeting-management'

/**
 * Çoklu seçim → virgüllü liste (Spring `List<Long>` bağlaması; axios dizi köşeli-ayraç ekini
 * bağlamaz). Boş seçim → parametre **hiç gönderilmez** → sunucu "arşivli hariç tüm durumlar" der.
 */
function csvIds(ids: number[] | undefined): string | undefined {
  return ids?.length ? ids.join(',') : undefined
}

/**
 * Görev listesi. Sayfa boyutu **ana görev** sayar: `includeSubTasks` toplam kayıt sayısını
 * değiştirmez, yalnız sayfa içeriğine alt görev satırları ekler ve her biri üstünün hemen ardından
 * gelir (istemci yeniden sıralamaz).
 */
export async function getMeetingManagementTasks(
  query: MeetingManagementQuery,
): Promise<PagedResponse<MeetingManagementTask>> {
  const { data } = await apiClient.get<PagedResponse<MeetingManagementTask>>(`${BASE}/tasks`, {
    params: pruneEmpty({
      page: query.page ?? 0,
      size: query.size ?? 20,
      statusIds: csvIds(query.statusIds),
      delayedStatusIds: csvIds(query.delayedStatusIds),
      onTimeStatusIds: csvIds(query.onTimeStatusIds),
      creatorUserId: query.creatorUserId,
      mainResponsibleEmployeeId: query.mainResponsibleEmployeeId,
      supportManagerEmployeeId: query.supportManagerEmployeeId,
      meetingGroupIds: csvIds(query.meetingGroupIds),
      // Sunucu varsayılanı `true`; kutu kaldırıldığında `false` AÇIKÇA gönderilmeli.
      includeDirectTasks: query.includeDirectTasks,
      includeSubTasks: query.includeSubTasks ? true : undefined,
      sort: query.sort,
    }),
  })
  return data
}

/**
 * Bir görevin dosya ekleri (meta) — satırdaki ataca tıklanınca açılan pop-up için. İçerik yine ortak
 * indirme ucundan (`/management/attachments/{id}`) çekilir; bu uç yalnız meta döndürür.
 *
 * Dönen tip **Görev Detay'ın Dokümanlar bölümüyle aynıdır** (`ActionDocument`): iki yüzey aynı
 * kaynağı gösterir, kendi tipini kuran taraf sözleşmedeki bir değişikliği sessizce yutardı.
 */
export async function getTaskAttachments(id: number): Promise<ActionDocument[]> {
  const { data } = await apiClient.get<ActionDocument[]>(`${BASE}/tasks/${id}/attachments`)
  return data
}

/** Tablonun yazı boyu ayarları (px); aralık kontrolü sunucuda, istemci değeri olduğu gibi uygular. */
export async function getMeetingManagementDisplaySettings(): Promise<MeetingManagementDisplaySettings> {
  const { data } = await apiClient.get<MeetingManagementDisplaySettings>(`${BASE}/display-settings`)
  return data
}

/**
 * "Görevi Oluşturan" süzgecinin seçenekleri. Ekranın **diğer** süzgeçleri uygulanmaz: seçenekler
 * kullanıcı filtre değiştirdikçe kaybolmaz.
 */
export async function getTaskCreators(): Promise<TaskCreatorOption[]> {
  const { data } = await apiClient.get<TaskCreatorOption[]>(`${BASE}/creators`)
  return data
}

/** "Toplantı Grubu" süzgecinin seçenekleri — listelenebilir görevi olan gruplar, ada göre sıralı. */
export async function getMeetingGroupOptions(): Promise<MeetingGroupOption[]> {
  const { data } = await apiClient.get<MeetingGroupOption[]>(`${BASE}/meeting-groups`)
  return data
}

/**
 * Kişi süzgeçlerinin seçenekleri. `role` **zorunludur** ve iki liste bilinçle ayrıdır: birleşik liste
 * dönseydi kullanıcı "Ana Sorumlu" kutusundan yalnızca Süreç Destek Yöneticisi olan birini seçebilir
 * ve sebebi ekranda görünmeyen boş bir sonuç alırdı.
 */
export async function getResponsibleOptions(role: ResponsibleRole): Promise<MinutePerson[]> {
  const { data } = await apiClient.get<MinutePerson[]>(`${BASE}/responsibles`, { params: { role } })
  return data
}

/**
 * Seçili görevleri **toplu** kapat (`Tamamlandı → Kapatıldı`). Tek işlemdir: tümü başarılı olur ya da
 * **hiçbiri** yazılmaz. Her öğe listede dönen `modDate`'i aynen taşır — bayat bir damga tüm işlemi
 * `409` ile durdurur.
 */
export async function closeMeetingManagementTasks(items: CloseItem[]): Promise<void> {
  await apiClient.put(`${BASE}/close`, { items })
}

/**
 * Bir görevi yeni hedef teslim tarihiyle yeniden aç (`Tamamlandı → Aksiyon Bekleniyor`).
 *
 * Görev **başına** tek istektir (çoklu seçimde kuyruk istemcide yürür): her görev için ayrı tarih ve
 * ayrı not girilebilmesinin karşılığı budur.
 */
export async function reopenMeetingManagementTask(
  id: number,
  body: ReopenRequest,
): Promise<void> {
  await apiClient.put(`${BASE}/tasks/${id}/reopen`, body)
}
